import { db } from '@/data/firebase/config';
import { columnsUser } from '@/domain/env/colums-user';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { User } from '@/domain/types/user';
import Button from '@/presentation/components/button';
import Drawer from '@/presentation/components/drawer';
import Spinner from '@/presentation/components/spinner';
import Table from '@/presentation/components/table';
import {
  and,
  collection,
  DocumentData,
  DocumentSnapshot,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { FaArrowDown, FaFileExcel } from 'react-icons/fa';
import { FiPlus } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import AddUserForm from './form-add';
import { excludeSelfRegisteredUsersFilter } from './visible-users-filter';

const UsersPage = () => {
  const [isOpenAddUser, setIsOpenAddUser] = useState(false);
  const [user, setUser] = useState<User>();
  const [isEdit, setIsEdit] = useState(false);
  const [data, setData] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastVisible, setLastVisible] =
    useState<DocumentSnapshot<DocumentData> | null>(null);
  const [limitRows, setLimitRows] = useState(10);
  const [dataFiltered, setDataFiltered] = useState<User[]>([]);
  const [searchCriteria, setSearchCriteria] = useState<string>('document');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [status, setStatus] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState(false);

  const searchOptions = [
    { label: 'Documento', value: 'document' },
    { label: 'Nombre', value: 'name' },
    { label: 'Apellido', value: 'lastname' },
    { label: 'Email', value: 'email' },
    { label: 'Área', value: 'area' },
    { label: 'Departamento', value: 'department' },
    { label: 'Cargo', value: 'position' },
    { label: 'Unidad', value: 'unit' },
    { label: 'Rol', value: 'role' },
  ];

  const handleAddUser = () => {
    setUser({
      id: '',
      document: '',
      name: '',
      lastname: '',
      email: '',
      role: 'employee',
      isEnabled: false,
      department: '',
      area: '',
      position: '',
      unit: '',
    });
    setIsEdit(false);
    setIsOpenAddUser(true);
  };

  const handleEditUser = (user: User) => {
    setUser(user);
    setIsEdit(true);
    setIsOpenAddUser(true);
  };

  const exportUsers = async () => {
    setIsExporting(true);

    try {
      const collectionRef = collection(db, DOCUMENTS_DB.USERS);
      // Sin limit(): a diferencia del listado en pantalla (que pagina con
      // limitRows/startAfter), el export debe traer TODOS los usuarios que
      // cumplan el filtro de estado seleccionado, no solo la página cargada.
      const queryRef = query(
        collectionRef,
        where('isEnabled', '==', status),
        excludeSelfRegisteredUsersFilter(),
        orderBy('name'),
      );

      const snapshot = await getDocs(queryRef);
      const usersToExport = snapshot.docs.map((doc) => ({
        id: doc.id,
        document: doc.data().document,
        name: doc.data().name,
        lastname: doc.data().lastname,
        email: doc.data().email,
        role: doc.data().role || 'employee',
        isEnabled: doc.data().isEnabled,
        area: doc.data().area,
        department: doc.data().department,
        position: doc.data().position,
        unit: doc.data().unit,
      })) as User[];

      const sheetData = usersToExport.map((user) => ({
        Documento: user.document,
        Nombre: user.name,
        Apellido: user.lastname,
        Email: user.email,
        Departamento: user.department,
        Área: user.area,
        Cargo: user.position,
        Unidad: user.unit,
        Rol: user.role === 'administrator' ? 'Administrador' : 'Empleado',
        Estado: user.isEnabled ? 'Activo' : 'Inactivo',
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');
      XLSX.writeFile(
        workbook,
        `usuarios-${status ? 'activos' : 'inactivos'}.xlsx`,
      );
    } catch (error) {
      console.error('Error exportando usuarios:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const fetchData = useCallback(
    async (lastVisibleProp: DocumentSnapshot<DocumentData> | null = null) => {
      setIsLoading(true);

      const collectionRef = collection(db, DOCUMENTS_DB.USERS);

      const queryRef = query(
        collectionRef,
        where('isEnabled', '==', status),
        excludeSelfRegisteredUsersFilter(),
        orderBy('name'),
        limit(limitRows),
        startAfter(lastVisibleProp),
      );

      const snapshot = await getDocs(queryRef);

      const newData = snapshot.docs.map((doc) => ({
        id: doc.id,
        document: doc.data().document,
        name: doc.data().name,
        lastname: doc.data().lastname,
        email: doc.data().email,
        role: doc.data().role || 'employee',
        isEnabled: doc.data().isEnabled,
        area: doc.data().area,
        department: doc.data().department,
        position: doc.data().position,
        unit: doc.data().unit,
      })) as User[];

      if (lastVisibleProp) {
        setData((prev) => [...prev, ...newData]);
      } else {
        setData(newData);
      }

      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setIsLoading(false);
    },
    [limitRows, status],
  );

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      console.log('searchTerm', searchTerm);
      setIsLoading(true);
      setDataFiltered([]);

      if (!searchTerm.trim()) {
        fetchData();
        return;
      }

      const collectionRef = collection(db, DOCUMENTS_DB.USERS);
      let queryRef;

      if (searchCriteria === 'document') {
        const documentFilter = or(
          where('document', '==', searchTerm),
          where('document', '==', parseInt(searchTerm)),
        );

        queryRef = query(
          collectionRef,
          and(documentFilter, excludeSelfRegisteredUsersFilter()),
        );
      } else {
        queryRef = query(
          collectionRef,
          orderBy(searchCriteria),
          where(searchCriteria, '>=', searchTerm.toLowerCase()),
          where(searchCriteria, '<=', searchTerm.toLowerCase() + '\uf8ff'),
          excludeSelfRegisteredUsersFilter(),
        );
      }

      try {
        const snapshot = await getDocs(queryRef);
        const filteredUsers = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as User[];

        setDataFiltered(filteredUsers);
      } catch (error) {
        console.error('Error en la búsqueda:', error);
        setDataFiltered([]);
      } finally {
        setIsLoading(false);
      }
    },
    [searchCriteria, fetchData],
  );

  const handleSaved = () => {
    setIsOpenAddUser(false);
    handleSearch(searchTerm);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    handleSearch(searchTerm);
  }, [searchTerm, handleSearch]);

  if (!data) return <div>No hay datos</div>;

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Empleados</h1>
        <div className="flex items-center gap-2">
          <Link to="/dashboard/users/update">
            <Button
              label="Activar o desactivar empleados"
              icon={<FaFileExcel />}
              onClick={() => {}}
              variant="secondary"
            />
          </Link>
          <Link to="/dashboard/users/upload">
            <Button
              label="Cargar empleados"
              icon={<FaFileExcel />}
              onClick={() => {}}
              variant="primary"
            />
          </Link>

          <Button
            label={`Exportar ${status ? 'activos' : 'inactivos'}`}
            icon={<FaFileExcel />}
            onClick={exportUsers}
            variant="secondary"
            isLoading={isExporting}
          />

          <Button
            label="Agregar empleado"
            icon={<FiPlus />}
            onClick={handleAddUser}
          />
        </div>
      </div>

      <div className="flex justify-end items-center gap-8">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Mostrar empleados</span>
          <select
            className="border border-gray-300 bg-white rounded-md p-2 text-sm"
            onChange={(e) => setStatus(e.target.value === 'true')}
            value={status.toString()}
          >
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Buscar por</span>
          <select
            className="border border-gray-300 bg-white rounded-md p-2 text-sm"
            onChange={(e) => setSearchCriteria(e.target.value)}
            value={searchCriteria}
          >
            {searchOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500"></span>
          <input
            type="text"
            className="border border-gray-300 bg-white rounded-md p-2 text-sm"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            placeholder="Escribe para buscar"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Cargar # de empleados</span>
          <select
            className="border border-gray-300 bg-white rounded-md p-2"
            onChange={(e) => setLimitRows(Number(e.target.value))}
            value={limitRows}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      <div>
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Spinner />
          </div>
        ) : (
          <>
            {/* Mostrar mensaje cuando no hay resultados de búsqueda */}
            {searchTerm.trim() && dataFiltered.length === 0 && !isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No se encontraron usuarios
                </h3>
                <p className="text-gray-500">
                  No hay empleados que coincidan con "{searchTerm}" en{' '}
                  {searchOptions
                    .find((option) => option.value === searchCriteria)
                    ?.label.toLowerCase()}
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 text-blue-600 hover:text-blue-800 underline"
                >
                  Limpiar búsqueda
                </button>
              </div>
            ) : /* Mostrar mensaje cuando no hay datos en general */
            !searchTerm.trim() && data.length === 0 && !isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="text-gray-400 text-6xl mb-4">👥</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No hay empleados {status ? 'activos' : 'inactivos'}
                </h3>
                <p className="text-gray-500">
                  Comienza agregando empleados al sistema
                </p>
              </div>
            ) : (
              <>
                <Table
                  columns={columnsUser}
                  data={dataFiltered.length > 0 ? dataFiltered : data}
                  onRowClick={handleEditUser}
                />
                <div className="flex justify-center pt-4">
                  <Button
                    label="Mostrar más"
                    variant="ghost"
                    icon={<FaArrowDown />}
                    positionIcon="right"
                    onClick={() => fetchData(lastVisible)}
                    className="w-fit"
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      <Drawer
        isOpen={isOpenAddUser}
        onClose={() => setIsOpenAddUser(false)}
        title={isEdit ? 'Editar empleado' : 'Agregar empleado'}
      >
        <AddUserForm user={user} isEdit={isEdit} onSaved={handleSaved} />
      </Drawer>
    </div>
  );
};

export default UsersPage;
